<template>
  <div class="sc-chat-window"
       :class="windowClasses"
       :style="{fontFamily: colors.font}"
  >
    <Header
      v-if="showHeader"
      :showCloseButton="showCloseButton"
      :title="title"
      :imageUrl="headerImageUrl"
      :onClose="handleClose"
      :colors="colors"
      :disableUserListToggle="disableUserListToggle"
      :forceFullScreen="forceFullScreen"
      :wideView="wideView"
      :modalView="modalView || modalCollapsing"
      @userList="handleUserListToggle"
      @toggleWideView="toggleWideView"
      @toggleModalView="toggleModalView"
    >
      <template>
        <slot name="header">
        </slot>
      </template>
    </Header>
    <UserList
      v-if="showUserList"
      :participants="participants"
    />

    <status-bar v-if="statusBarVisible" :type="statusBarType" :colors="colors" class="sc-status-bar"
                @click="onStatusBarClick"/>

    <MessageList
      :style="statusBarVisible ? 'margin-top: -27px;' : ''"
      v-if="!showUserList"
      :messages="messages"
      :participants="participants"
      :showTypingIndicator="showTypingIndicator"
      :typingStatusText="typingStatusText"
      :colors="colors"
      :alwaysScrollToBottom="alwaysScrollToBottom"
      :messageStyling="messageStyling"
      :onButtonPressed="onButtonPressed"
      :onMessageFeedback="onMessageFeedback"
      :storageType="storageType"
      :sessionId="sessionId"
      :onSubmit="onUserInputSubmit"
      @remove="$emit('remove', $event)"
    >
      <template v-slot:user-avatar="scopedProps">
        <slot name="user-avatar" :user="scopedProps.user" :message="scopedProps.message">
        </slot>
      </template>
      <template v-slot:text-message-body="scopedProps">
        <slot name="text-message-body" :message="scopedProps.message" :messageText="scopedProps.messageText"
              :messageColors="scopedProps.messageColors" :me="scopedProps.me">
        </slot>
      </template>
      <template v-slot:system-message-body="scopedProps">
        <slot name="system-message-body" :message="scopedProps.message">
        </slot>
      </template>
      <template v-slot:text-message-toolbox="scopedProps">
        <slot name="text-message-toolbox" :message="scopedProps.message" :me="scopedProps.me">
        </slot>
      </template>
    </MessageList>

    <UserInput
      v-show="!showUserList && displayUserInput"
      :onSubmit="onUserInputSubmit"
      :onButtonPressed="onButtonPressed"
      :showFile="showFile"
      :showVoice="showVoice"
      :placeholder="$t('interface.writeMessage')"
      @onType="$emit('onType')"
      :colors="colors"
      :powered-by-amio-config="poweredByAmioConfig"
      :class="userInputAnimationClass"
    />
  </div>
</template>

<script>
import Header from './Header.vue'
import MessageList from './MessageList.vue'
import UserInput from './UserInput.vue'
import UserList from './UserList.vue'
import StatusBar from './StatusBar'
import { EventBus, Event } from '../../util/event-bus'
import USER_EVENT from '../../util/user-event.constants'
import StatusBarType from '../../enum/status-bar-type.enum'
import { ANIMATION_TIME } from '@/util/constants'
import { sleep } from '@/util/mixins'

export default {
  components: {
    Header,
    MessageList,
    UserInput,
    UserList,
    StatusBar
  },
  props: {
    showCloseButton: {
      type: Boolean,
      default: true
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showFile: {
      type: Boolean,
      default: false
    },
    showVoice: {
      type: Boolean,
      default: false
    },
    participants: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    headerImageUrl: {
      type: String,
      default: ''
    },
    onUserInputSubmit: {
      type: Function,
      required: true
    },
    onButtonPressed: {
      type: Function,
      required: true
    },
    onMessageFeedback: {
      type: Function,
      required: true
    },
    storageType: {
      type: String,
      default: 'local'
    },
    sessionId: {
      type: String,
      default: null
    },
    onClose: {
      type: Function,
      required: true
    },
    messageList: {
      type: Array,
      default: () => []
    },
    isOpen: {
      type: Boolean,
      default: () => false
    },
    showTypingIndicator: {
      type: Boolean,
      required: true
    },
    typingStatusText: {
      type: String,
      default: ''
    },
    colors: {
      type: Object,
      required: true
    },
    alwaysScrollToBottom: {
      type: Boolean,
      required: true
    },
    messageStyling: {
      type: Boolean,
      required: true
    },
    disableUserListToggle: {
      type: Boolean,
      default: false
    },
    forceFullScreen: {
      type: Boolean,
      default: false
    },
    hideLauncher: {
      type: String,
      default: ''
    },
    poweredByAmioConfig: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      showUserList: false,
      statusBarType: null,
      imageExpanded: false,
      wideView: false,
      modalView: false,
      modalCollapsing: false,
      animateWidth: false,
      animateModal: false,
      animationClass: 'slide-in-bottom',
      displayUserInput: true,
      // Only used for validation show/hide — not on widget open, so the input
      // opens with the window fade instead of sliding in separately
      userInputAnimationClass: ''
    }
  },
  computed: {
    messages () {
      const messages = this.messageList

      return messages
    },
    statusBarVisible () {
      return this.statusBarType !== null
    },
    hideLauncherOnChatOpen () {
      return this.hideLauncher === 'chatOpen'
    },
    windowClasses () {
      return [
        this.hideLauncherOnChatOpen ? `${this.animationClass}` : 'fade-in',
        {
          'image-expanded': this.imageExpanded,
          'forced-full-screen': this.forceFullScreen,
          'wide-view': this.wideView,
          'modal-view': this.modalView,
          'modal-collapsing': this.modalCollapsing,
          'animate-width': this.animateWidth,
          'animate-modal': this.animateModal,
          closed: !this.isOpen
        }
      ]
    }
  },
  methods: {
    handleUserListToggle (showUserList) {
      this.showUserList = showUserList
    },
    onStatusBarClick () {
      EventBus.$emit(Event.STATUS_BAR_CLICK, this.statusBarType)
    },
    showStatusBar (type) {
      this.statusBarType = type
    },
    hideStatusBar (type) {
      if (this.statusBarType === type) {
        this.statusBarType = null
      }
    },
    setImageExpanded (status) {
      this.imageExpanded = status
    },
    collapseModal () {
      // Keep the panel centered while the parent shrinks to the bottom-right
      // so the motion reverses the open animation instead of snapping to the corner first
      this.animateModal = true
      this.modalCollapsing = true
      this.modalView = false
      EventBus.$emit(Event.MODAL_VIEW_TOGGLED, false)
      setTimeout(() => {
        this.animateModal = false
        this.modalCollapsing = false
      }, ANIMATION_TIME)
    },
    toggleWideView () {
      if (this.modalView || this.modalCollapsing) {
        this.collapseModal()
      }
      this.animateWidth = true
      this.wideView = !this.wideView
      EventBus.$emit(Event.WIDE_VIEW_TOGGLED, this.wideView)
      setTimeout(() => {
        this.animateWidth = false
      }, ANIMATION_TIME)
    },
    toggleModalView () {
      if (this.modalCollapsing) return

      if (this.wideView) {
        this.wideView = false
        this.animateWidth = false
        EventBus.$emit(Event.WIDE_VIEW_TOGGLED, false)
      }

      if (this.modalView) {
        this.collapseModal()
        return
      }

      this.modalCollapsing = false
      this.animateModal = true
      this.modalView = true
      EventBus.$emit(Event.MODAL_VIEW_TOGGLED, true)
      setTimeout(() => {
        this.animateModal = false
      }, ANIMATION_TIME)
    },
    async handleClose () {
      if (this.hideLauncherOnChatOpen) await this.resolveAnimation()

      this.onClose(USER_EVENT.HEADER_CLICK_CLOSE)
    },
    async resolveAnimation () {
      this.animationClass = 'slide-out-bottom'
      await sleep(ANIMATION_TIME)
      this.animationClass = 'slide-in-bottom'
    },

    async hideUserInput () {
      this.userInputAnimationClass = 'slide-out-bottom-500'
      await sleep(ANIMATION_TIME)
      this.displayUserInput = false
      this.userInputAnimationClass = ''
    },

    async showUserInput () {
      this.displayUserInput = true
      this.userInputAnimationClass = 'slide-in-bottom-500'
      await sleep(ANIMATION_TIME)
      this.userInputAnimationClass = ''
    }
  },
  watch: {
    isOpen (open) {
      if (!open && this.wideView) {
        this.wideView = false
        this.animateWidth = false
        EventBus.$emit(Event.WIDE_VIEW_TOGGLED, false)
      }
      if (!open && (this.modalView || this.modalCollapsing)) {
        this.modalView = false
        this.modalCollapsing = false
        this.animateModal = false
        EventBus.$emit(Event.MODAL_VIEW_TOGGLED, false)
      }
    }
  },
  mounted () {
    EventBus.$on(Event.CHAT_UNREAD_MESSAGES, () => this.showStatusBar(StatusBarType.UNREAD_MESSAGES))
    EventBus.$on(Event.CHAT_SCROLL_BOTTOM, () => this.hideStatusBar(StatusBarType.UNREAD_MESSAGES))
    EventBus.$on(Event.CHAT_ONLINE, () => this.hideStatusBar(StatusBarType.OFFLINE))
    EventBus.$on(Event.CHAT_OFFLINE, () => this.showStatusBar(StatusBarType.OFFLINE))
    EventBus.$on(Event.IMAGE_EXPANDED, this.setImageExpanded)
    EventBus.$on(Event.INPUT_SHOW_VALIDATION, this.hideUserInput)
    EventBus.$on(Event.INPUT_HIDE_VALIDATION, this.showUserInput)
  }
}
</script>
